/*

& "Pick a location for your broadcast" (3 Nov 2026, the app's Select agents step, paid broadcasts only): Near by Areas
& (this device's location and a reach radius, 5 km to start, 1 to 50) or Entire City (Bengaluru or Mysuru, the app's
& list). Sent as `target_location` (lib/broadcasts/createBroadcastCore.ts keeps it as intent; the agent pool isn't
& filtered by it yet, in the app either). Location access declined: near_by goes without coordinates, as the app sends it.

*/

import { MenuItem, Radio, Select, Typography } from "@mui/material";
import React from "react";

export type BroadcastTargetLocation =
	| { scope: "near_by"; latitude?: number; longitude?: number; radius_km?: number }
	| { scope: "entire_city"; city: string };

/* The app's list (CreateBroadcastViewModel.BROADCAST_CITIES); the server says which may be picked (`cities`). */
export const BROADCAST_CITIES: Array<string> = ["Bengaluru", "Mysuru"];
const RADII_KM: Array<number> = [1, 2, 5, 10, 20, 50];

const BroadcastLocationPicker = ({
	value,
	onChange,
	locale,
	cities = [],
}: {
	value: BroadcastTargetLocation;
	onChange: (value: BroadcastTargetLocation) => void;
	locale: string | undefined;
	/* Each city's reach from the pricing route: a city under the smallest band can't be picked (4 Nov 2026). */
	cities?: Array<{ city: string; agents: number; available: boolean }>;
}) => {
	const tr = (en: string, ar: string, kn: string): string => (locale === "ar-AE" ? ar : locale === "kn-IN" ? kn : en);
	const [locating, setLocating] = React.useState<boolean>(false);
	/* Until the server answers, every city reads as available; once it has, only the ones it allows. */
	const isAvailable = (city: string): boolean => {
		const found = cities.find((row) => row.city.toLowerCase() === city.toLowerCase());
		return cities.length === 0 || Boolean(found?.available);
	};
	const firstAvailable: string = BROADCAST_CITIES.find(isAvailable) || "";
	/* The chosen city became unavailable: back to Near by Areas rather than a broadcast nobody can be sold. */
	React.useEffect(() => {
		if (value.scope === "entire_city" && cities.length > 0 && !isAvailable(value.city)) onChange({ scope: "near_by", radius_km: 5 });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cities]);

	const pickNearBy = (radius_km: number) => {
		onChange({ scope: "near_by", radius_km });
		if (typeof navigator === "undefined" || !navigator.geolocation) return;
		setLocating(true);
		navigator.geolocation.getCurrentPosition(
			(position) => {
				setLocating(false);
				onChange({ scope: "near_by", radius_km, latitude: position.coords.latitude, longitude: position.coords.longitude });
			},
			() => setLocating(false),
			{ timeout: 10000 },
		);
	};

	const option = (selected: boolean, label: string, onPick: () => void, extra?: React.ReactNode) => (
		<div
			role="radio"
			aria-checked={selected}
			tabIndex={0}
			onClick={onPick}
			onKeyDown={(event) => event.key === "Enter" && onPick()}
			style={{ display: "flex", alignItems: "center", gap: "0.25rem", flexWrap: "wrap", cursor: "pointer" }}
		>
			<Radio
				size="small"
				checked={selected}
				tabIndex={-1}
			/>
			<Typography sx={{ fontSize: "0.875rem" }}>{label}</Typography>
			{selected ? extra : null}
		</div>
	);

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", width: "100%", padding: "1rem 0 0" }}>
			<Typography sx={{ fontSize: "1rem", fontWeight: 600 }}>
				{tr("Pick a location for your broadcast", "اختر موقعًا للبث", "ನಿಮ್ಮ ಪ್ರಸಾರಕ್ಕೆ ಸ್ಥಳವನ್ನು ಆರಿಸಿ")}
			</Typography>
			<Typography sx={{ fontSize: "0.8125rem", color: "#666666" }}>
				{tr("Location determines where you broadcast your message", "يحدد الموقع مكان بث رسالتك", "ನಿಮ್ಮ ಸಂದೇಶವನ್ನು ಎಲ್ಲಿ ಪ್ರಸಾರ ಮಾಡಬೇಕೆಂದು ಸ್ಥಳ ನಿರ್ಧರಿಸುತ್ತದೆ")}
			</Typography>
			{option(
				value.scope === "near_by",
				tr("Near by Areas", "المناطق القريبة", "ಹತ್ತಿರದ ಪ್ರದೇಶಗಳು"),
				() => value.scope !== "near_by" && pickNearBy(5),
				<>
					<Select
						size="small"
						value={value.scope === "near_by" ? value.radius_km || 5 : 5}
						onChange={(event) => pickNearBy(Number(event.target.value))}
						onClick={(event) => event.stopPropagation()}
						sx={{ fontSize: "0.8125rem", marginInlineStart: "0.5rem" }}
					>
						{RADII_KM.map((km) => (
							<MenuItem
								key={km}
								value={km}
							>
								{`${km} km`}
							</MenuItem>
						))}
					</Select>
					{locating ? (
						<Typography sx={{ fontSize: "0.75rem", color: "#757575", marginInlineStart: "0.5rem" }}>
							{tr("Finding your location...", "جارٍ تحديد موقعك...", "ನಿಮ್ಮ ಸ್ಥಳವನ್ನು ಹುಡುಕಲಾಗುತ್ತಿದೆ...")}
						</Typography>
					) : null}
				</>,
			)}
			{option(
				value.scope === "entire_city",
				tr("Entire City", "المدينة بأكملها", "ಇಡೀ ನಗರ"),
				() => value.scope !== "entire_city" && firstAvailable && onChange({ scope: "entire_city", city: firstAvailable }),
				<Select
					size="small"
					value={value.scope === "entire_city" ? value.city : firstAvailable}
					onChange={(event) => onChange({ scope: "entire_city", city: String(event.target.value) })}
					onClick={(event) => event.stopPropagation()}
					sx={{ fontSize: "0.8125rem", marginInlineStart: "0.5rem" }}
				>
					{BROADCAST_CITIES.map((city) => (
						<MenuItem
							key={city}
							value={city}
							disabled={!isAvailable(city)}
						>
							{isAvailable(city) ? city : `${city} (${tr("not enough agents", "عدد الوكلاء غير كافٍ", "ಸಾಕಷ್ಟು ಏಜೆಂಟ್‌ಗಳಿಲ್ಲ")})`}
						</MenuItem>
					))}
				</Select>,
			)}
		</div>
	);
};

export default BroadcastLocationPicker;
